import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ImageUpload } from '@/components/shared/ImageUpload';
import { sectionService, CreateSectionRequest } from '@/features/admin/services/sectionService';
import { uploadService } from '@/features/admin/services/uploadService';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ArrowRight, Layers } from 'lucide-react';

const createSectionSchema = z.object({
  name: z.string().min(1, 'اسم القسم مطلوب').max(200, 'الاسم طويل جداً'),
  description: z.string().optional(),
  displayOrder: z.number().min(0).default(0),
});

type CreateSectionFormData = z.infer<typeof createSectionSchema>;

export const CreateSectionPage = () => {
  const navigate = useNavigate();
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [iconUrl, setIconUrl] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateSectionFormData>({
    resolver: zodResolver(createSectionSchema),
    defaultValues: {
      displayOrder: 0,
    },
  });

  const onSubmit = async (data: CreateSectionFormData) => {
    try {
      const request: CreateSectionRequest = {
        name: data.name,
        description: data.description || undefined,
        imageUrl: imageUrl || undefined,
        iconUrl: iconUrl || undefined,
        displayOrder: data.displayOrder,
        isActive: true,
      };

      await sectionService.createSection(request);
      toast.success('تم إنشاء القسم بنجاح');
      navigate('/admin/sections');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل إنشاء القسم');
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-accent-greenLight/20 rounded-lg">
          <Layers className="w-8 h-8 text-accent-greenDark" />
        </div>
        <div>
          <h1 className="text-3xl font-bold text-accent-dark mb-1">إضافة قسم جديد</h1>
          <p className="text-accent-dark/70">الأقسام هي المستوى الأعلى في تنظيم الفئات</p>
        </div>
      </div>

      <Card className="border-2 border-accent-greenLight/20">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-accent-dark">معلومات القسم</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                اسم القسم *
              </label>
              <Input
                {...register('name')}
                placeholder="مثال: الرياضة، التاريخ، العلوم..."
                error={errors.name?.message}
                className="text-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                وصف القسم
              </label>
              <textarea
                {...register('description')}
                placeholder="وصف مختصر للقسم..."
                rows={3}
                className="w-full px-4 py-2 border-2 border-accent-greenLight/30 rounded-lg focus:outline-none focus:border-secondary text-accent-dark bg-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                صورة القسم
              </label>
              <ImageUpload
                value={imageUrl || undefined}
                onChange={(url) => setImageUrl(url)}
                onUpload={uploadService.uploadSectionImage}
                placeholder="ارفع صورة للقسم"
              />
              <p className="text-xs text-gray-500 mt-1">صورة كبيرة للعرض في الصفحة الرئيسية</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                أيقونة القسم (لوجو)
              </label>
              <ImageUpload
                value={iconUrl || undefined}
                onChange={(url) => setIconUrl(url)}
                onUpload={uploadService.uploadSectionImage}
                placeholder="ارفع أيقونة للقسم"
              />
              <p className="text-xs text-gray-500 mt-1">أيقونة صغيرة للعرض في القوائم</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-accent-dark mb-2">
                ترتيب العرض
              </label>
              <Input
                type="number"
                {...register('displayOrder', { valueAsNumber: true })}
                placeholder="0"
                error={errors.displayOrder?.message}
                className="text-lg"
              />
              <p className="text-xs text-gray-500 mt-1">الأقسام ذات الرقم الأصغر تظهر أولاً</p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/sections')}
                className="flex-1"
              >
                إلغاء
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-secondary hover:bg-secondary-dark text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    إنشاء القسم
                    <ArrowRight className="w-4 h-4 mr-2" />
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
